import { create } from 'zustand';

import { loadFontById } from '../engine/font-loader';
import { computeVectorGeometry } from '../engine/weld-and-score';
import { PRESET_TEMPLATES } from '../presets';
import type {
  BackerConfig,
  FontDefinition,
  MaterialType,
  TextLine,
  UnitType,
  VectorCadState,
  WeldScoreConfig,
} from '../types';

interface VectorCadStore extends VectorCadState {
  // Line manipulations
  addLine: (text?: string) => void;
  updateLine: (id: string, updates: Partial<TextLine>) => void;
  removeLine: (id: string) => void;
  moveLine: (id: string, direction: 'up' | 'down') => void;
  selectLine: (id: string | null) => void;

  // Global settings
  setLineSpacingMultiplier: (multiplier: number) => void;
  setOverallAlignment: (alignment: 'left' | 'center' | 'right') => void;
  updateWeldScoreConfig: (updates: Partial<WeldScoreConfig>) => void;
  updateBackerConfig: (updates: Partial<BackerConfig>) => void;
  setMaterial: (material: MaterialType) => void;
  setUnit: (unit: UnitType) => void;
  toggleGrid: () => void;
  toggleRulers: () => void;
  toggleDimensions: () => void;

  // Presets & Fonts
  applyPreset: (presetId: string) => void;
  addCustomFont: (fontDef: FontDefinition) => void;

  // Geometry Engine
  recalculate: () => Promise<void>;
}

const defaultPreset = PRESET_TEMPLATES[0];

export const useVectorCadStore = create<VectorCadStore>((set, get) => {
  let debounceTimeout: any = null;

  const triggerDebouncedRecalculate = () => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      void get().recalculate();
    }, 40);
  };

  return {
    lines: defaultPreset.lines.map((l, idx) => ({
      ...l,
      id: `line-${idx + 1}-${Date.now()}`,
    })),
    lineSpacingMultiplier: 1.15,
    overallAlignment: 'center',
    weldScoreConfig: {
      weldEnabled: true,
      scoreEnabled: true,
      cutColor: '#FF0000',
      scoreColor: '#0055FF',
      scoreStrokeWidthMm: 0.2,
      connectDots: true,
      bridgeWidthMm: 1.5,
      bridgeOverhangMm: 0.4,
      minScoreLengthMm: 0.3,
      ...defaultPreset.weldScoreConfig,
    },
    backerConfig: {
      type: defaultPreset.backerConfig.type || 'outset',
      offsetMm: defaultPreset.backerConfig.offsetMm ?? 4.5,
      cornerJoin: 'round',
      scoreGuidesOnBacker: true,
      guideColor: '#00CCCC',
      hangerHoles: false,
      hangerHoleCount: 2,
      hangerHoleDiameterMm: 4.0,
      hangerHoleInsetMm: 6.0,
      baseBarHeightMm: 14.0,
      baseBarExtendMm: 8.0,
      standSlots: false,
      cakeTopperProngLengthMm: 65.0,
      cakeTopperProngWidthMm: 6.5,
      ...defaultPreset.backerConfig,
    },
    material: defaultPreset.material,
    unit: 'mm',
    showGrid: true,
    showRulers: true,
    showDimensions: true,
    selectedLineId: null,
    customFonts: [],
    isCalculating: false,
    activePresetId: defaultPreset.id,
    geometryResult: null,

    addLine: (text = 'New Quote Line') => {
      const newLine: TextLine = {
        id: `line-${Date.now()}`,
        text,
        fontId: 'dancing-script',
        fontSizeMm: 35,
        letterSpacingPercent: -3,
        alignment: get().overallAlignment,
      };
      set((state) => ({
        lines: [...state.lines, newLine],
        selectedLineId: newLine.id,
      }));
      triggerDebouncedRecalculate();
    },

    updateLine: (id, updates) => {
      set((state) => ({
        lines: state.lines.map((l) => (l.id === id ? { ...l, ...updates } : l)),
      }));
      triggerDebouncedRecalculate();
    },

    removeLine: (id) => {
      set((state) => {
        const remaining = state.lines.filter((l) => l.id !== id);
        return {
          lines: remaining.length > 0 ? remaining : state.lines,
          selectedLineId: state.selectedLineId === id ? null : state.selectedLineId,
        };
      });
      triggerDebouncedRecalculate();
    },

    moveLine: (id, direction) => {
      set((state) => {
        const idx = state.lines.findIndex((l) => l.id === id);
        if (idx < 0) return state;
        const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (targetIdx < 0 || targetIdx >= state.lines.length) return state;

        const newLines = [...state.lines];
        const temp = newLines[idx];
        newLines[idx] = newLines[targetIdx];
        newLines[targetIdx] = temp;
        return { lines: newLines };
      });
      triggerDebouncedRecalculate();
    },

    selectLine: (id) => set({ selectedLineId: id }),

    setLineSpacingMultiplier: (multiplier) => {
      set({ lineSpacingMultiplier: multiplier });
      triggerDebouncedRecalculate();
    },

    setOverallAlignment: (alignment) => {
      set({ overallAlignment: alignment });
      triggerDebouncedRecalculate();
    },

    updateWeldScoreConfig: (updates) => {
      set((state) => ({
        weldScoreConfig: { ...state.weldScoreConfig, ...updates },
      }));
      triggerDebouncedRecalculate();
    },

    updateBackerConfig: (updates) => {
      set((state) => ({
        backerConfig: { ...state.backerConfig, ...updates },
      }));
      triggerDebouncedRecalculate();
    },

    setMaterial: (material) => set({ material }),
    setUnit: (unit) => set({ unit }),
    toggleGrid: () => set((s) => ({ showGrid: !s.showGrid })),
    toggleRulers: () => set((s) => ({ showRulers: !s.showRulers })),
    toggleDimensions: () => set((s) => ({ showDimensions: !s.showDimensions })),

    applyPreset: (presetId) => {
      const preset = PRESET_TEMPLATES.find((p) => p.id === presetId);
      if (!preset) return;

      set((state) => ({
        activePresetId: preset.id,
        lines: preset.lines.map((l, idx) => ({
          ...l,
          id: `line-${idx + 1}-${Date.now()}`,
        })),
        weldScoreConfig: { ...state.weldScoreConfig, ...preset.weldScoreConfig },
        backerConfig: { ...state.backerConfig, ...preset.backerConfig },
        material: preset.material,
      }));
      triggerDebouncedRecalculate();
    },

    addCustomFont: (fontDef) => {
      set((state) => ({
        customFonts: [...state.customFonts, fontDef],
      }));
    },

    recalculate: async () => {
      const {
        lines,
        customFonts,
        lineSpacingMultiplier,
        overallAlignment,
        weldScoreConfig,
        backerConfig,
      } = get();
      if (lines.length === 0) return;

      set({ isCalculating: true });

      try {
        // Load fonts in parallel
        const fontMap = new Map<string, any>();
        const uniqueFontIds = Array.from(new Set(lines.map((l) => l.fontId)));

        await Promise.all(
          uniqueFontIds.map(async (fontId) => {
            try {
              const font = await loadFontById(fontId, customFonts);
              fontMap.set(fontId, font);
            } catch (err) {
              console.error(`Failed to load font ${fontId}`, err);
            }
          }),
        );

        // Run Geometry Engine
        const result = computeVectorGeometry({
          lines,
          fontMap,
          lineSpacingMultiplier,
          overallAlignment,
          weldScoreConfig,
          backerConfig,
        });

        set({ geometryResult: result, isCalculating: false });
      } catch (err) {
        console.error('[VectorCADStore] Recalculate failed:', err);
        set({ isCalculating: false });
      }
    },
  };
});
